import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalLoader } from '@doccareplus/ui';
import { ShieldAlert } from 'lucide-react';
import { useClinicAccessQuery } from '@features/clinic-access';
import { hasClinicCapability, type PermissionMode } from '@features/clinic-access';

interface RequirePermissionProps {
  /** Any one of these permissions grants access (OR semantics), matching the nav gating helper. */
  permissions: readonly string[];
  /** Optional plan feature that must also be enabled. */
  feature?: string;
  /** Whether any listed permission or every listed permission is required. Defaults to any. */
  permissionMode?: PermissionMode;
}

/**
 * Route-level authorization guard. Nav links are permission-gated, but that only hides links — a
 * user could still deep-link to a protected route. This wraps such routes so the page never renders
 * (and its queries never fire) unless the active role actually holds the permission. Uses the same
 * `hasClinicCapability` helper as the sidebar so route access and nav visibility never drift.
 */
export function RequirePermission({
  permissions,
  feature,
  permissionMode = 'any',
}: RequirePermissionProps) {
  const accessQuery = useClinicAccessQuery();

  if (accessQuery.isLoading) {
    return <GlobalLoader />;
  }

  if (!hasClinicCapability(accessQuery.data, permissions, feature, permissionMode)) {
    return <AccessDenied />;
  }

  return <Outlet />;
}

function AccessDenied() {
  const { t } = useTranslation('common');

  return (
    <section className="flex min-h-[calc(100vh-150px)] items-center justify-center">
      <div className="w-full max-w-[560px] rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-8 text-center shadow-[var(--shadow-1)]">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[12px] bg-[var(--danger-bg)] text-[var(--danger)]">
          <ShieldAlert className="h-5 w-5" aria-hidden="true" />
        </div>
        <h2 className="mt-5 text-[22px] font-semibold text-[var(--text)]">{t('workspace.accessDenied.title')}</h2>
        <p className="mx-auto mt-2 max-w-[440px] text-[13.5px] leading-relaxed text-[var(--text-2)]">
          {t('workspace.accessDenied.description')}
        </p>
      </div>
    </section>
  );
}
